import React from 'react';
import * as yup from 'yup';
import Dropzone from 'react-dropzone';
import {
  Typography,
  Grid,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { BreathingSparkAnimation } from 'components/Animations';
import { SparkForm, SparkTextField } from 'sharedComponents/SparkForm';
import { SparkImageLoader } from 'sharedComponents/UI';
import { NextIcon } from 'components/SVG';
import { useUpdateBoardNode } from 'hooks/Board';
import type { BoardNodeType } from 'types/entityTypes/boardNodeTypes';
import type { Accept, FileRejection } from 'react-dropzone';
import type { BoardNodeFiles, BoardNodes } from 'types/BoardNode';
import {
  isValidAudioUrl,
  isValidVideoUrl,
  readFileAsBase64,
} from './boardNodeHelpers';

type Props = {
  isSelected: boolean;
  nodeType: BoardNodeType | undefined;
  isAddingNewNode?: boolean;
  boardNode: BoardNodes;
  handleUpdateNode?: (node: BoardNodes) => void;
};

type FormValues = {
  url: string;
};

function EmptyNodeContainer({
  isSelected,
  nodeType,
  isAddingNewNode,
  boardNode,
  handleUpdateNode,
}: Props) {
  const { mutateAsync: updateBoardNode, isLoading } = useUpdateBoardNode(
    boardNode.id,
    boardNode.attributes.boardID
  );

  const isVideoNode = nodeType?.attributes?.key === 'video';
  const isAudioNode = nodeType?.attributes?.key === 'audio';

  // Get accepted MIME types for audio validation
  const getAcceptedMimeTypes = (): string[] => {
    if (!nodeType?.attributes?.dropAcceptFileTypes) return [];
    try {
      return JSON.parse(nodeType.attributes.dropAcceptFileTypes);
    } catch {
      return [];
    }
  };

  const acceptedMimeTypes = getAcceptedMimeTypes();

  const validationSchema = yup.object().shape({
    url: yup
      .string()
      .url('Invalid URL')
      .test('media-url', function (value) {
        if (!value) return true; // Allow empty values

        if (isVideoNode) {
          return (
            isValidVideoUrl(value) ||
            this.createError({
              message: 'Only YouTube and Loom URLs are allowed for video nodes',
            })
          );
        }
        if (isAudioNode) {
          return (
            isValidAudioUrl(value, acceptedMimeTypes) ||
            this.createError({
              message: `Only direct audio file URLs with supported formats are allowed`,
            })
          );
        }
        return true; // For other node types, any valid URL is acceptable
      }),
  });

  const initialValues = {
    url: '',
  };

  const handleSuccessfulUpload = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ): Promise<void> => {
    try {
      // Read file data as base64 for each accepted file
      const fileDataPromises: Promise<string>[] = acceptedFiles.map((file) =>
        readFileAsBase64(file)
      );
      const base64Results: string[] = await Promise.all(fileDataPromises);

      // Create file objects with base64 file data
      const filesWithData: BoardNodeFiles[] = acceptedFiles.map(
        (file: File, index: number) => ({
          uploadType: 'file',
          fileData: base64Results[index], // Base64-encoded file content
          originalFilename: file.name,
          fileSize: file.size,
          mimeType: file.type,
        })
      );

      const updatedNode = await updateBoardNode({
        files: filesWithData,
      });

      if (handleUpdateNode) {
        handleUpdateNode(updatedNode?.data);
      }
    } catch (error) {
      console.error('Error reading file data:', error);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    const updatedNode = await updateBoardNode({
      files: [
        {
          uploadType: 'external',
          url: values.url,
        },
      ],
    });

    if (handleUpdateNode) {
      handleUpdateNode(updatedNode?.data);
    }
  };

  const parseAcceptedFileTypes = (
    fileTypesString?: string
  ): Accept | undefined => {
    if (!fileTypesString) return undefined;

    try {
      const fileTypesArray: string[] = JSON.parse(fileTypesString);
      const acceptObject: Accept = {};

      fileTypesArray.forEach((mimeType) => {
        acceptObject[mimeType] = [];
      });

      return acceptObject;
    } catch (error) {
      console.error('Error parsing accepted file types:', error);
      return undefined;
    }
  };

  if (!nodeType) {
    return null;
  }

  const acceptedFileTypes: Accept | undefined = parseAcceptedFileTypes(
    nodeType?.attributes?.dropAcceptFileTypes
  );
  const MAX_FILE_SIZE = nodeType?.attributes?.fileSizeMBLimit * 1024 * 1024;

  const renderEmptyNodeContent = () => {
    if (isLoading) return <CircularProgress />;
    return (
      <Grid
        item
        xs={12}
        container
        alignItems='center'
        justifyItems={'center'}
        direction='column'
        gap={2}
      >
        {isAddingNewNode ? (
          <Box>
            <BreathingSparkAnimation />
          </Box>
        ) : (
          <SparkImageLoader
            src={nodeType?.attributes.prettyIconURL}
            alt='empty'
            width={50}
            height={50}
          />
        )}

        <Typography
          variant='body1'
          className='color-text'
          fontFamily='Mont-SemiBold'
        >
          {isAddingNewNode
            ? 'Initializing'
            : isSelected
              ? nodeType?.attributes.emptyStateHeader
              : nodeType?.attributes.emptyStateInactiveHeader}
        </Typography>
        <Typography variant='body1'>
          {isAddingNewNode
            ? 'Just a Sec...'
            : isSelected
              ? nodeType?.attributes.emptyStateSubheader
              : nodeType?.attributes.emptyStateInactiveSubHeader}
        </Typography>
      </Grid>
    );
  };

  return (
    <Grid container padding={2}>
      {nodeType?.attributes?.acceptsDropUpload ? (
        <>
          <Dropzone
            onDrop={handleSuccessfulUpload}
            multiple={true}
            accept={acceptedFileTypes}
            maxSize={MAX_FILE_SIZE}
          >
            {({ getRootProps, getInputProps }) => (
              <Box
                {...getRootProps()}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                {renderEmptyNodeContent()}
                <input {...getInputProps()} />
              </Box>
            )}
          </Dropzone>
        </>
      ) : (
        renderEmptyNodeContent()
      )}
      {nodeType?.attributes.acceptsURLInput && (
        <SparkForm<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSave={handleSubmit}
          sx={{
            pt: 2,
            justifyContent: 'center',
            width: '100%',
            height: 'auto',
          }}
        >
          <SparkTextField
            label=''
            name='url'
            size={10}
            placeholder={nodeType.attributes.urlPlaceholderText}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    edge='end'
                    aria-label='submit'
                    type='submit'
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                    }}
                  >
                    <NextIcon color='gray' />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </SparkForm>
      )}
    </Grid>
  );
}

export default EmptyNodeContainer;
